/**
 * How much of the family home's land the exemption does NOT cover.
 *
 * THE RULE. The principal home is exempt from the Age Pension assets test together with up to
 * 2 hectares of adjacent land ON THE SAME TITLE. Two things fall outside that:
 *
 *   1. LAND ON A SEPARATE TITLE. A title that does not have the house on it is not part of the
 *      principal home, so it is an ordinary assessable asset. No test, no exception.
 *
 *   2. LAND BEYOND 2 HECTARES on the same title. Assessable, UNLESS the EXTENDED LAND USE TEST
 *      applies — which needs BOTH a long-term (20-year) continuous attachment to the property AND
 *      effective use of the land. Where it applies, all the adjacent land on that title is exempt
 *      however large it is.
 *
 * Sources: Social Security Guide 4.6.8 (Assessing Land Adjacent to the Principal Home), 4.6.8.50
 * (general provisions for the extended land use test) and 4.6.8.60 (long-term 20-year continuous
 * attachment); DVA CLIK, Extended Land Use Test. "Effective use" is satisfied where the land is
 * used to its potential to generate income, or where it has little or no such potential.
 *
 * WHY THIS EXISTS. Until 2026-08-22 all four of the property questions on the Living Arrangements
 * step were dead data: `landSize`, `landSizeValue`, `separateTitles`, `separateTitlesValue` and the
 * farm/business follow-ups were written to the fact-find and READ BY NOTHING. They appeared only as
 * displayed facts in the SOA. A client with $500,000 of land on a second title had it counted in
 * neither the Age Pension assets test nor the aged care means test — overstating the pension and
 * understating the care fee, both in the client's favour and both wrong.
 *
 * WHERE THE MONEY LANDS. This value belongs in `assessableAssetsNoHome`, not with the home. That
 * matters for residential aged care, where the HOME is counted only up to the capped amount
 * ($214,884): land outside the exemption is not the home, so it is assessed at its full value with
 * no cap. Putting it in with the home would silently cap it too.
 *
 * NET WORTH IS UNAFFECTED. These values are a PORTION of the home value already entered, which is
 * what the fact-find labels say ("of the home value above"). Adding them to net worth as well would
 * count the same land twice.
 */
import type { FactFind } from './factFindTypes.ts';

export interface HomeLandAssessment {
    /** Land beyond 2 hectares on the home's title, counted only when the extended land use test fails. */
    excessLand: number;
    /** Land on a separate title. Always counted — it is not part of the principal home. */
    separateTitleLand: number;
    /** What to add to assessable assets. */
    total: number;
    /** True when the extended land use test exempts the land beyond 2 hectares. */
    extendedLandUseTestApplies: boolean;
}

const numberOf = (value: unknown): number => {
    const n = parseFloat(String(value ?? ''));
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Does the extended land use test exempt this client's land beyond 2 hectares?
 *
 * Both limbs are required. A working farm answers both through the questions the fact-find already
 * asked before this rule existed — `farmContinuouslyWorked` is the 20-year limb, and primary
 * production is effective use by definition — so a farming client is not asked the same thing twice.
 *
 * UNANSWERED MEANS ASSESSABLE, deliberately. That is the conservative direction on both sides of the
 * tool: it shows a lower Age Pension and a higher care fee than an over-generous assumption would,
 * so a client is never advised on the strength of an exemption nobody confirmed.
 */
export function extendedLandUseTestApplies(factFind: FactFind): boolean {
    const home = factFind?.home || {};
    const twentyYearAttachment = home.longTermLandAttachment === true || home.farmContinuouslyWorked === true;
    const effectiveUse = home.effectiveLandUse === true || home.primaryProductionUse === true;
    return twentyYearAttachment && effectiveUse;
}

/** The part of the home's land that counts in the assets test. */
export function assessableHomeLandOf(factFind: FactFind): HomeLandAssessment {
    const home = factFind?.home || {};

    const separateTitleLand = home.separateTitles ? numberOf(home.separateTitlesValue) : 0;

    const excessLandValue = home.landSize ? numberOf(home.landSizeValue) : 0;
    const testApplies = excessLandValue > 0 && extendedLandUseTestApplies(factFind);
    const excessLand = testApplies ? 0 : excessLandValue;

    return {
        excessLand,
        separateTitleLand,
        total: excessLand + separateTitleLand,
        extendedLandUseTestApplies: testApplies,
    };
}
